import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiError } from "@/lib/api";
import { AppLangToggle } from "@/components/AppLangToggle";

// Full-screen cinematic background (the approved woman / lakeside café image).
const BG = "https://static.prod-images.emergentagent.com/jobs/3085e34f-2862-4361-89ba-1e9784a7f039/images/a360a0e525e2d0a5d5578bb9a49357a1d4e2638ca8f453f9e6c200b4f6ea3384.jpeg";
const MARK = {
  WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
  WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
  WebkitMaskPosition: "center", maskPosition: "center",
  background: "linear-gradient(135deg,#bcddff 0%,#6aa6f7 30%,#2f6fe0 62%,#17368f 100%)",
  filter: "drop-shadow(0 4px 10px rgba(23,42,110,0.35))",
};

// ONE cinematic full-screen composition. Text contrast has priority:
//  - soft light glow behind the top logo/tagline (over the bright sky)
//  - progressively darker gradient toward the bottom behind the headline + form + actions
function AuthShell({ title, subtitle, children, footer, bg = BG }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen w-full flex justify-center bg-[#0B1220]">
      <div className="relative w-full max-w-[460px] min-h-screen overflow-hidden">
        {/* full-screen background image */}
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "56% 22%" }} />
        {/* top light glow so dark logo/tagline stays readable over the sky */}
        <div className="absolute inset-x-0 top-0 h-[34%] pointer-events-none"
          style={{ background: "radial-gradient(120% 90% at 50% 6%, rgba(255,255,255,0.66) 0%, rgba(255,255,255,0.28) 40%, transparent 72%)" }} />
        {/* bottom darkening so white headline, fields and buttons are high-contrast */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, transparent 0%, transparent 24%, rgba(6,12,26,0.06) 34%, rgba(6,12,26,0.32) 45%, rgba(6,12,26,0.60) 57%, rgba(6,12,26,0.82) 74%, rgba(6,12,26,0.94) 100%)" }} />

        <div className="relative z-10 min-h-screen flex flex-col px-6"
          style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 40px)", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 26px)" }}>
          {/* interface-language selector */}
          <div className="absolute right-5 z-20" style={{ top: "calc(env(safe-area-inset-top,0px) + 16px)" }}><AppLangToggle dark /></div>
          {/* brand */}
          <div className="flex flex-col items-center shrink-0">
            <div style={{ ...MARK, width: 84, height: 84 * (243 / 490) * 1.95 }} className="block" data-testid="auth-mark" />
            <p className="font-heading font-extrabold text-[#0B1727] mt-0.5" style={{ letterSpacing: "0.34em", fontSize: 21, paddingLeft: "0.34em", textShadow: "0 1px 10px rgba(255,255,255,0.65)" }}>MELLETIC</p>
            <p className="mt-1 text-[14px] font-bold" style={{ textShadow: "0 1px 8px rgba(255,255,255,0.6)" }}>
              <span className="text-[#334155]">{t("tagline_a")} </span><span className="text-[#2563EB]">{t("tagline_b")}</span>
            </p>
          </div>

          {/* spacer lets the scenery breathe */}
          <div className="flex-1 min-h-[80px]" />

          {/* lower content over the dark gradient */}
          <div className="shrink-0">
            <h1 className="font-heading font-extrabold" style={{ color: "#ffffff", fontSize: "clamp(34px,10vw,46px)", lineHeight: 1.02, textShadow: "0 2px 22px rgba(0,0,0,0.6)" }}>{title}</h1>
            <p className="mt-1.5 text-[17px]" style={{ color: "rgba(255,255,255,0.92)", textShadow: "0 1px 16px rgba(0,0,0,0.6)" }}>{subtitle}</p>
            <div className="mt-5">{children}</div>
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ icon: Icon, type, ...props }) {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  const inputType = isPw ? (show ? "text" : "password") : type;
  const tid = props["data-testid"];
  return (
    <div className="flex items-center gap-3 rounded-2xl px-4 py-4 border border-white/60 shadow-[0_14px_34px_-16px_rgba(0,0,0,0.6)] focus-within:ring-2 focus-within:ring-[#2563EB]"
      style={{ background: "rgba(255,255,255,0.94)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
      {Icon && <Icon size={19} className="text-[#2563EB]" />}
      <input type={inputType} {...props} className="flex-1 bg-transparent outline-none text-[#0F172A] placeholder:text-slate-500" />
      {isPw && (
        <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
          data-testid={tid ? `${tid}-toggle` : "password-toggle"} aria-label={show ? "Hide password" : "Show password"}
          className="text-slate-500">{show ? <EyeOff size={19} /> : <Eye size={19} />}</button>
      )}
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(t("welcome_back"));
      // Everyone (incl. admin) enters as a normal learner; admins reach the
      // dashboard only when they choose Settings → Admin.
      nav("/home");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("login_failed"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth_login_title")} subtitle={t("auth_login_sub")}
      footer={(
        <>
          <div className="flex items-center gap-3 my-4">
            <span className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.4)" }} />
            <span className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>{t("auth_or")}</span>
            <span className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.4)" }} />
          </div>
          <Link to="/register" data-testid="to-register"
            className="block text-center w-full py-4 rounded-2xl border font-bold active:scale-[0.99] transition-transform"
            style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.14)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
            {t("create_account")}
          </Link>
        </>
      )}>
      <form onSubmit={submit} className="space-y-3">
        <Field icon={Mail} type="email" required placeholder={t("email_ph")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <Field icon={Lock} type="password" required placeholder={t("password_ph")} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        <div className="text-right pt-0.5">
          <Link to="/forgot" data-testid="forgot-link" className="text-sm font-bold" style={{ color: "#ffffff", textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>{t("forgot_q")}</Link>
        </div>
        <button disabled={loading} data-testid="login-submit"
          className="w-full font-bold py-4 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition-transform mt-1"
          style={{ color: "#ffffff", background: "linear-gradient(100deg,#1d4ed8,#2563eb 55%,#22aee8)", boxShadow: "0 18px 40px -12px rgba(37,99,235,0.75)" }}>
          <span style={{ color: "#ffffff" }}>{loading ? t("logging_in") : t("log_in")}</span> {!loading && <ArrowRight size={19} style={{ color: "#ffffff" }} />}
        </button>
      </form>
    </AuthShell>
  );
}

export { AuthShell };
